// src/components/modals/TableSelectionModal.jsx
import { useState, useEffect, useCallback } from 'react'
import { Button } from '../ui/Button'
import { X, Table, Users, MapPin, Search, RefreshCw, Filter } from 'lucide-react'
import TableService from '../../services/tableService'
import BranchService from '../../services/branchService'
import ReadyToCloseModal from './ReadyToCloseModal'
import ModalLoader from '../ui/ModalLoader'
import { cn } from '../../utils/cn'

const TableSelectionModal = ({ 
  isOpen, 
  onClose, 
  onTableSelect, 
  selectedTableId = null,
  title = "Select Table",
  userBranchId = null // Add user's branch ID prop
}) => {
  const [tables, setTables] = useState([])
  const [filteredTables, setFilteredTables] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Ready to Close Modal state
  const [showReadyToCloseModal, setShowReadyToCloseModal] = useState(false)
  const [selectedOccupiedTable, setSelectedOccupiedTable] = useState(null)
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  // Removed selectedBranch since we only show user's branch
  const [availabilityFilter, setAvailabilityFilter] = useState('all')
  const [capacityFilter, setCapacityFilter] = useState('all')
  const [showFilters, setShowFilters] = useState(false)

  // Filter tables function using useCallback
  const filterTables = useCallback(() => {
    let filtered = [...tables]

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(table => 
        table.number?.toLowerCase().includes(search) ||
        table.branchDetails?.name?.toLowerCase().includes(search)
      )
    }

    // Branch filter - removed since we only show user's branch tables

    // Availability filter
    if (availabilityFilter !== 'all') {
      filtered = filtered.filter(table => table.availability === availabilityFilter)
    }

    // Capacity filter
    if (capacityFilter !== 'all') {
      filtered = filtered.filter(table => {
        const capacity = parseInt(table.capacity)
        switch (capacityFilter) {
          case '1-2': return capacity <= 2
          case '3-4': return capacity >= 3 && capacity <= 4
          case '5-8': return capacity >= 5 && capacity <= 8
          case '9+': return capacity >= 9
          default: return true
        }
      })
    }

    setFilteredTables(filtered)
  }, [tables, searchTerm, availabilityFilter, capacityFilter]) // Remove selectedBranch

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch tables with branch filtering using query parameters
      const tableParams = {
        page: 1,
        limit: 100,
        isActive: true
      }
      
      // Add branch filter if userBranchId is provided
      if (userBranchId) {
        tableParams.branch = userBranchId
      }
      
      // Fetch tables and branches in parallel
      const [tablesResponse, branchesResponse] = await Promise.all([
        TableService.getAllTables(tableParams),
        BranchService.getAllBranches()
      ])

      // Handle tables response
      if (tablesResponse?.success) {
        const tablesData = tablesResponse.data?.tables || tablesResponse.data?.docs || tablesResponse.data || []
        setTables(tablesData)
        console.log('✅ Tables loaded for branch:', tablesData.length)
      } else {
        throw new Error('Failed to fetch tables')
      }

      // Handle branches response
      if (branchesResponse?.success) {
        const branchesData = branchesResponse.data?.branches || branchesResponse.data?.docs || branchesResponse.data || []
        const activeBranches = branchesData.filter(branch => branch.isActive)
        setBranches(activeBranches)
        console.log('✅ Branches loaded:', activeBranches.length)
      }

    } catch (error) {
      console.error('❌ Error fetching table data:', error)
      setError('Failed to load tables. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [userBranchId]) // Add useCallback dependencies

  // Fetch tables and branches when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen, fetchData]) // Include fetchData in dependencies

  // Filter tables whenever filters change
  useEffect(() => {
    filterTables()
  }, [filterTables])

  const handleTableSelect = (table) => {
    console.log('🪑 Table clicked:', table)
    
    // If table is occupied, show Ready to Close modal instead
    if (table.availability === 'occupied') {
      console.log('🪑 Occupied table clicked, showing Ready to Close modal')
      setSelectedOccupiedTable(table)
      setShowReadyToCloseModal(true)
      return
    }
    
    // Prevent reserved tables from being selected
    if (table.availability === 'reserved') {
      console.log('🪑 Reserved table clicked - cannot be selected')
      return
    }
    
    // Only allow available tables to be selected
    console.log('🪑 Table selected for order:', table)
    onTableSelect?.(table)
    onClose()
  }

  const handleRefresh = () => {
    fetchData()
  }

  const handleTableStatusUpdated = (tableId, newStatus) => {
    console.log('🪑 Table status updated:', { tableId, newStatus })
    
    // Update the local tables state
    setTables(prevTables => 
      prevTables.map(table => 
        table._id === tableId 
          ? { ...table, availability: newStatus }
          : table
      )
    )
    
    // Close the Ready to Close modal
    setShowReadyToCloseModal(false)
    setSelectedOccupiedTable(null)
  }

  const handleCloseReadyToCloseModal = () => {
    setShowReadyToCloseModal(false)
    setSelectedOccupiedTable(null)
  }

  const clearFilters = () => {
    setSearchTerm('')
    // Removed selectedBranch since we only show user's branch
    setAvailabilityFilter('all')
    setCapacityFilter('all')
  }

  const getBranchName = (table) => {
    if (table.branchDetails?.name) {
      return table.branchDetails.name
    }
    
    const branch = branches.find(b => {
      const branchId = typeof table.branch === 'object' ? table.branch._id : table.branch
      return b._id === branchId
    })
    
    return branch?.name || 'Unknown Branch'
  }

  const getBranchNameById = (branchId) => {
    const branch = branches.find(b => b._id === branchId)
    return branch?.name || 'Current Branch'
  }

  const getTableStatusColor = (table) => {
    if (!table.isActive) return 'bg-gray-200 text-gray-500'
    
    switch (table.availability) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
      case 'reserved':
        return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
      case 'occupied':
        return 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
    }
  }

  const getTableStatusText = (table) => {
    if (!table.isActive) return 'Inactive'
    
    switch (table.availability) {
      case 'available': return 'Available'
      case 'reserved': return 'Reserved'
      case 'occupied': return 'Occupied'
      default: return table.availability || 'Unknown'
    }
  }

  const getAvailableTablesCount = () => {
    return filteredTables.filter(table => table.availability === 'available' && table.isActive).length
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Table className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500">
                {getAvailableTablesCount()} of {filteredTables.length} tables available
                {userBranchId && branches.length > 0 && (
                  <span className="ml-2">• {getBranchNameById(userBranchId)}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Table number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Branch Filter - Hidden since we only show user's branch */}
              {/* Removed branch filter as we only show tables from user's branch */}

              {/* Availability Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="occupied">Occupied</option>
                </select>
              </div>

              {/* Capacity Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                <select
                  value={capacityFilter}
                  onChange={(e) => setCapacityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Sizes</option>
                  <option value="1-2">1-2 people</option>
                  <option value="3-4">3-4 people</option>
                  <option value="5-8">5-8 people</option>
                  <option value="9+">9+ people</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="mt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {loading ? (
            <ModalLoader title="Loading tables..." />
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">
                <X className="h-12 w-12 mx-auto" />
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={handleRefresh}>
                Try Again
              </Button>
            </div>
          ) : filteredTables.length === 0 ? (
            <div className="text-center py-12">
              <Table className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No tables found</p>
              <p className="text-sm text-gray-500">Try adjusting your filters or refresh the data</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredTables.map(table => (
                <button
                  key={table._id}
                  onClick={() => handleTableSelect(table)}
                  disabled={!table.isActive || table.availability === 'reserved'}
                  className={cn(
                    "p-4 rounded-lg border-2 transition-all duration-200 text-center",
                    "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2",
                    getTableStatusColor(table),
                    selectedTableId === table._id && "ring-2 ring-orange-500 ring-offset-2",
                    !table.isActive && "opacity-50 cursor-not-allowed",
                    table.availability === 'reserved' && "opacity-50 cursor-not-allowed", // Reserved tables disabled
                    table.availability === 'occupied' && "cursor-pointer" // Allow clicking occupied tables
                  )}
                >
                  <div className="font-bold text-lg mb-1">{table.number}</div>
                  <div className="text-xs mb-2">{getTableStatusText(table)}</div>
                  
                  <div className="flex items-center justify-center text-xs text-gray-600 mb-1">
                    <Users className="h-3 w-3 mr-1" />
                    {table.capacity}
                  </div>
                  
                  <div className="flex items-center justify-center text-xs text-gray-500 truncate">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{getBranchName(table)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing {filteredTables.length} tables
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Ready to Close Modal */}
      <ReadyToCloseModal
        isOpen={showReadyToCloseModal}
        onClose={handleCloseReadyToCloseModal}
        table={selectedOccupiedTable}
        onTableStatusUpdated={handleTableStatusUpdated}
      />
    </div>
  )
}

export default TableSelectionModal
